import { Card } from "@/components/ui/card";

export const PIPELINE_STAGES = {
  // Intake & Qualification
  NEW_INTAKE: { label: "New Intake", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", order: 0 },
  DISQUALIFIED: { label: "Disqualified", color: "bg-red-500/20 text-red-400 border-red-500/30", order: 1 },
  NURTURE: { label: "Nurture", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", order: 2 },
  QUALIFIED: { label: "Qualified", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", order: 3 },
  
  // Qualification Call
  QUAL_CALL_BOOKED: { label: "Call Booked", color: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30", order: 4 },
  NO_SHOW: { label: "No Show", color: "bg-[#007a9e]/20 text-[#2a9ec0] border-[#007a9e]/30", order: 5 },
  QUALIFIED_TO_BUY: { label: "Ready to Buy", color: "bg-[var(--brand)]/20 text-[var(--brand)] border-[var(--brand)]/30", order: 6 },
  
  // Free Assessment
  FREE_ASSESSMENT_COMPLETE: { label: "Free Assessment Done", color: "bg-teal-500/20 text-teal-400 border-teal-500/30", order: 7 },
  
  // Gate 1: Paid Assessment ($4,999)
  ASSESSMENT_PURCHASED: { label: "Paid Assessment Purchased", color: "bg-[var(--brand)]/30 text-[var(--brand)] border-[var(--brand)]/40", order: 8 },
  ASSESSMENT_IN_PROGRESS: { label: "Paid Assessment In Progress", color: "bg-[#007a9e]/20 dark:bg-yellow-500/20 text-[#007a9e] dark:text-yellow-400 border-[#007a9e]/30 dark:border-yellow-500/30", order: 9 },
  ASSESSMENT_DELIVERED: { label: "Paid Assessment Delivered", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", order: 10 },
  
  // Scope Confirmation
  SCOPE_CONFIRMED: { label: "Scope Confirmed", color: "bg-sky-500/20 text-sky-400 border-sky-500/30", order: 11 },
  
  // Gate 2: SOW Approval
  SOW_APPROVED: { label: "SOW Approved", color: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30", order: 12 },
  
  // Gate 3: Implementation Project ($9,999)
  PROJECT_IN_DELIVERY: { label: "Project In Delivery", color: "bg-violet-500/20 text-violet-400 border-violet-500/30", order: 13 },
  PROJECT_DELIVERED: { label: "Project Delivered", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", order: 14 },
  
  // Gate 4: Retainer ($2,500/mo)
  RETAINER_OFFERED: { label: "Retainer Offered", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", order: 15 },
  RETAINER_ACTIVE: { label: "Retainer Active", color: "bg-emerald-500/30 text-emerald-400 border-emerald-500/40", order: 16 },
  
  // Risk states
  AT_RISK: { label: "At Risk", color: "bg-red-500/20 text-red-400 border-red-500/30", order: 17 },
  CHURNED: { label: "Churned", color: "bg-gray-500/20 text-gray-400 border-gray-500/30", order: 18 },
};

const STAGE_MIGRATION = {
  new: "NEW_INTAKE",
  scheduled: "QUAL_CALL_BOOKED",
  completed_initial: "QUALIFIED",
  qualified: "QUALIFIED_TO_BUY",
  paid_assessment: "ASSESSMENT_PURCHASED",
  assessment_complete: "ASSESSMENT_DELIVERED",
  sow_approved: "SOW_APPROVED",
  sow_signed: "SOW_APPROVED",
  action_plan_purchased: "PROJECT_IN_DELIVERY",
  action_plan_delivered: "PROJECT_DELIVERED",
  proposal_sent: "RETAINER_OFFERED",
  deposit_paid: "PROJECT_IN_DELIVERY",
  implementation_active: "PROJECT_IN_DELIVERY",
  retained: "RETAINER_ACTIVE",
  closed: "CHURNED",
};

export const normalizeStage = (stage) => {
  if (PIPELINE_STAGES[stage]) return stage;
  return STAGE_MIGRATION[stage] || "NEW_INTAKE";
};

export const stageLabel = (s) => {
  const normalized = normalizeStage(s);
  return PIPELINE_STAGES[normalized]?.label || s;
};

export const stageOrder = {
  NEW_INTAKE: 0,
  DISQUALIFIED: 1,
  NURTURE: 2,
  QUALIFIED: 3,
  QUAL_CALL_BOOKED: 4,
  NO_SHOW: 5,
  QUALIFIED_TO_BUY: 6,
  FREE_ASSESSMENT_COMPLETE: 7,
  ASSESSMENT_PURCHASED: 8,
  ASSESSMENT_IN_PROGRESS: 9,
  ASSESSMENT_DELIVERED: 10,
  SCOPE_CONFIRMED: 11,
  SOW_APPROVED: 12,
  PROJECT_IN_DELIVERY: 13,
  PROJECT_DELIVERED: 14,
  RETAINER_OFFERED: 15,
  RETAINER_ACTIVE: 16,
  AT_RISK: 17,
  CHURNED: 18,
  // Legacy mappings
  new: 0,
  scheduled: 4,
  completed_initial: 3,
  qualified: 6,
  paid_assessment: 8,
  assessment_complete: 10,
};

export const stageAtLeast = (current, target) =>
  (stageOrder[current] ?? -1) >= (stageOrder[target] ?? 999);

export const StagePill = ({ stage }) => {
  const normalized = normalizeStage(stage);
  const stageConfig = PIPELINE_STAGES[normalized] || PIPELINE_STAGES.NEW_INTAKE;
  
  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium",
        stageConfig.color,
      ].join(" ")}
    >
      {stageConfig.label}
    </span>
  );
};

export const MetricCard = ({ label, value, sub, accent = false }) => {
  return (
    <div
      className={`rounded-[12px] border bg-white p-5 transition-all ${
        accent ? "border-[#bfdbfe] ring-1 ring-[#1a56db]/10" : "border-[#e5e7eb]"
      }`}
    >
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9ca3af]">
        {label}
      </div>
      <div className="mt-2 text-[28px] font-bold tracking-[-0.01em] text-[#111827] leading-none">
        {value}
      </div>
      {sub ? <div className="mt-2 text-[12px] text-[#6b7280]">{sub}</div> : null}
    </div>
  );
};
